import { type HalfPlane } from '../expression';
import { type Vec2, vec } from '../math';

/**
 * The miền nghiệm of a system of linear inequalities.
 *
 * ## What the figure has to say
 *
 * "Biểu diễn miền nghiệm của hệ bất phương trình" is the whole of one lesson in lớp 10, and the
 * answer is a picture: the boundary lines drawn, and the set of points satisfying every
 * inequality shaded. Two conventions carry the meaning and both matter — the shading says
 * *which* side, and a **dashed** boundary says the line itself is not in the solution while a
 * solid one says it is. A figure that gets the second wrong answers a different question.
 *
 * ## Why the region is clipped rather than drawn
 *
 * A half-plane is infinite and paper is not. The intersection of several is a convex set that
 * may be bounded (the usual exercise, a polygon with three or four corners) or may run off the
 * page in one direction. Clipping the frame by each half-plane in turn handles both without a
 * special case: what comes back is always a convex polygon, and its edges are of two kinds —
 * pieces of real boundaries, and pieces of the frame, which are an artefact of the paper and
 * must not be drawn as though they meant something.
 *
 * That is why the boundaries are returned separately rather than left to the polygon's own
 * outline. An outline would draw the frame edges too, and a reader would see a closed region
 * where the truth is a region that goes on.
 */

export interface Region {
  /** The shaded set, clipped to the frame. Empty when no point satisfies every inequality. */
  readonly vertices: readonly Vec2[];
  /** One per inequality, as two points spanning the frame — never the frame's own edges. */
  readonly boundaries: readonly {
    readonly from: Vec2;
    readonly to: Vec2;
    readonly strict: boolean;
  }[];
}

/**
 * A box far larger than any frame, used to find the region's corners before a frame exists.
 *
 * The statement has to choose a window, and it cannot do that without knowing where the corners
 * are — so the corners are found first, inside a box big enough that a bounded region's corners
 * are all strictly inside it. A vertex sitting *on* this box is not a corner of the region; it
 * is the region running off to infinity, and it is discarded rather than framed.
 */
const UNBOUNDED = 1e4;

/** Points closer than this are the same point, in mathematical units. */
const SAME = 1e-9;

/** The frame for a system that gives no clue about its own size — every boundary through 0. */
const DEFAULT_EXTENT = 4;

/** How much room to leave round the region, as a multiple of its own size. */
const PADDING = 1.25;

/** The rectangle a region is drawn inside, in mathematical units. */
export interface Frame {
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
}

const SQUARE = (box: number): Frame => ({ minX: -box, maxX: box, minY: -box, maxY: box });

function corners(frame: Frame): Vec2[] {
  return [
    vec.vec2(frame.minX, frame.minY),
    vec.vec2(frame.maxX, frame.minY),
    vec.vec2(frame.maxX, frame.maxY),
    vec.vec2(frame.minX, frame.maxY),
  ];
}

/**
 * Sutherland–Hodgman against one half-plane: keep what satisfies it, cut where it crosses.
 *
 * Convexity is what makes this correct in four lines — a convex polygon cut by a half-plane is
 * one convex polygon, never two — and every input here is convex by construction: a box, then
 * the result of cutting a box.
 */
function clip(polygon: readonly Vec2[], plane: HalfPlane): Vec2[] {
  const inside = (point: Vec2): boolean => plane.a * point.x + plane.b * point.y <= plane.c + SAME;
  const crossing = (from: Vec2, to: Vec2): Vec2 => {
    const before = plane.a * from.x + plane.b * from.y - plane.c;
    const after = plane.a * to.x + plane.b * to.y - plane.c;
    const t = before / (before - after);
    return vec.lerp(from, to, t);
  };

  const out: Vec2[] = [];
  for (let index = 0; index < polygon.length; index += 1) {
    const from = polygon[index];
    const to = polygon[(index + 1) % polygon.length];
    if (from === undefined || to === undefined) continue;

    if (inside(from)) {
      out.push(from);
      if (!inside(to)) out.push(crossing(from, to));
    } else if (inside(to)) {
      out.push(crossing(from, to));
    }
  }
  return out;
}

/** Drops the duplicate vertices that clipping leaves when a cut passes through a corner. */
function tidy(polygon: readonly Vec2[]): Vec2[] {
  const out: Vec2[] = [];
  for (const point of polygon) {
    const last = out[out.length - 1];
    if (last !== undefined && vec.distance(last, point) < SAME) continue;
    out.push(point);
  }
  const first = out[0];
  const last = out[out.length - 1];
  if (
    out.length > 1 &&
    first !== undefined &&
    last !== undefined &&
    vec.distance(first, last) < SAME
  ) {
    out.pop();
  }
  return out;
}

/**
 * The rectangle to draw the region in, or `null` when no point satisfies every inequality.
 *
 * ## Why it is a rectangle and not a reach
 *
 * Because most systems live in a corner of the plane. `x ≥ 0, y ≥ 0, 2x + y ≤ 4` is entirely in
 * the first quadrant, and a frame symmetric about the origin spends **three quarters of the
 * page** on ground no inequality has anything to say about — with the region itself drawn at a
 * quarter of the size the paper allows. A textbook draws the corner the problem is in.
 *
 * The origin is kept inside whatever the region does, because the axes are drawn along `x = 0`
 * and `y = 0`: a frame that excluded it would be a figure with no axes in it, and a miền nghiệm
 * is read by its coordinates.
 *
 * `null` rather than an empty rectangle for an unsatisfiable system. The caller reports it,
 * because a blank page and a page whose region happens to be off-screen look identical.
 */
export function regionFrame(planes: readonly HalfPlane[]): Frame | null {
  let polygon: readonly Vec2[] = corners(SQUARE(UNBOUNDED));
  for (const plane of planes) polygon = clip(polygon, plane);
  if (tidy(polygon).length < 3) return null;

  /*
   * Framed on the corners that are genuinely the region's, which means dropping any lying on the
   * far box: those are the region escaping, not a feature to fit on the page.
   */
  const finite = polygon.filter(
    (point) => Math.abs(point.x) < UNBOUNDED * 0.99 && Math.abs(point.y) < UNBOUNDED * 0.99
  );

  if (finite.length > 0) {
    return padded(
      Math.min(...finite.map((point) => point.x)),
      Math.max(...finite.map((point) => point.x)),
      Math.min(...finite.map((point) => point.y)),
      Math.max(...finite.map((point) => point.y))
    );
  }

  /*
   * A region with no corner at all — and that is the commonest exercise of the lot.
   *
   * "Biểu diễn miền nghiệm của bất phương trình 2x + 3y ≤ 6" is one half-plane: every corner of
   * the clipped box is an artefact of the box, so there is nothing for the frame to fit and the
   * first version refused to draw it. Refusing the most-set version of the exercise is not a
   * defensible reading of "no region".
   *
   * What a textbook frames instead is the **boundary**, by its intercepts — `2x + 3y = 6` is
   * drawn through (3; 0) and (0; 2) and the page is about that big. Which is also the only
   * information available: the region itself extends for ever, so its own extent cannot choose
   * a window and the line's must.
   */
  const marks: number[] = [];
  for (const plane of planes) {
    if (plane.a !== 0) marks.push(Math.abs(plane.c / plane.a));
    if (plane.b !== 0) marks.push(Math.abs(plane.c / plane.b));
  }

  const reach = marks.length === 0 ? 0 : Math.max(...marks);
  // A boundary through the origin has both intercepts at zero and still needs a page.
  return SQUARE(reach > SAME ? reach * PADDING : DEFAULT_EXTENT);
}

/**
 * Room round the region, and the origin inside it whatever happens.
 *
 * The padding is proportional to the region rather than a fixed number of units, because these
 * figures are drawn at every scale — a system in tenths and one in hundreds both want a margin
 * that looks like a margin.
 */
function padded(minX: number, maxX: number, minY: number, maxY: number): Frame {
  const margin = Math.max((maxX - minX) * (PADDING - 1), (maxY - minY) * (PADDING - 1), SAME);
  return {
    minX: Math.min(0, minX - margin),
    maxX: Math.max(0, maxX + margin),
    minY: Math.min(0, minY - margin),
    maxY: Math.max(0, maxY + margin),
  };
}

/**
 * Where the line through `origin` along `direction` enters and leaves the square frame.
 *
 * Liang–Barsky, which is the parametric form of the same question the polygon clipper answers:
 * each of the four edges bounds the parameter from one side, and what survives is the interval of
 * the line that is inside the box. `null` when the line misses the frame entirely — possible for
 * a boundary of a system whose region is in view while that particular line is not.
 */
function acrossBox(
  origin: Vec2,
  direction: Vec2,
  frame: Frame
): { readonly from: Vec2; readonly to: Vec2 } | null {
  let enter = -Infinity;
  let leave = Infinity;

  for (const [along, at, low, high] of [
    [direction.x, origin.x, frame.minX, frame.maxX],
    [direction.y, origin.y, frame.minY, frame.maxY],
  ] as const) {
    if (Math.abs(along) < SAME) {
      // Parallel to this pair of edges: either always between them or never.
      if (at < low || at > high) return null;
      continue;
    }
    const first = (low - at) / along;
    const second = (high - at) / along;
    enter = Math.max(enter, Math.min(first, second));
    leave = Math.min(leave, Math.max(first, second));
  }

  if (!(leave > enter)) return null;
  return {
    from: vec.addScaled(origin, direction, enter),
    to: vec.addScaled(origin, direction, leave),
  };
}

/** The shaded set and its boundaries, inside `frame`. */
export function regionIn(planes: readonly HalfPlane[], frame: Frame): Region {
  let polygon: readonly Vec2[] = corners(frame);
  for (const plane of planes) polygon = clip(polygon, plane);

  /*
   * Each boundary drawn across the whole frame, not only along the region's edge.
   *
   * That is what a textbook does and it is not decoration: the line `2x + y = 4` is the boundary
   * of its own half-plane everywhere, and drawing only the part that survives the *other*
   * inequalities would show a reader a shape rather than a system. It also keeps the dashed and
   * solid distinction legible, which is the whole grammar of the figure.
   */
  const boundaries = planes.flatMap((plane) => {
    const along = vec.normalize(vec.vec2(-plane.b, plane.a));
    const denominator = plane.a * plane.a + plane.b * plane.b;
    if (denominator === 0) return [];

    // The point of the line nearest the origin, which is on the line whatever the coefficients.
    const nearest = vec.vec2((plane.a * plane.c) / denominator, (plane.b * plane.c) / denominator);

    /*
     * Cut to the frame exactly, rather than drawn a length that ought to cover it.
     *
     * The first version used `extent * 1.45`, which is the half-diagonal of the square and so is
     * long enough for any angle. It is also *too* long for most: a boundary at 45° overshot the
     * corner, and the export frame is fitted to the content, so the shaded region stopped at the
     * axes while the lines carried on and the figure came out floating inside a larger page.
     * Solving for where the line meets the box gives the one length that is right at every angle.
     */
    const ends = acrossBox(nearest, along, frame);
    return ends === null ? [] : [{ ...ends, strict: plane.strict }];
  });

  return { vertices: tidy(polygon), boundaries };
}
